/**
 * A sport's mark: its own icon on its own colour.
 *
 * The colour is derived from the code, not stored and not random — the same sport
 * is the same colour in the switcher, the header and anywhere else this is used,
 * across reloads and machines, with nothing to configure. That is what makes it
 * usable as recognition rather than decoration.
 *
 * Solid rather than a gradient. The reference design's workspace marks are solid
 * too, and a two-stop gradient is the single most common tell of a screen nobody
 * designed.
 */

import { Icon, IconLayers, type IconName } from "@/components/Icons";

/** Hand-picked, and picked to be told apart: distinct hue *and* distinct
 *  lightness, so they still read as different in greyscale and to anyone who
 *  does not separate reds from greens. */
const PAINT = [
  { bg: "#1e3a8a", fg: "#dbeafe" }, // deep blue
  { bg: "#b45309", fg: "#fef3c7" }, // amber
  { bg: "#166534", fg: "#dcfce7" }, // green
  { bg: "#9d174d", fg: "#fce7f3" }, // rose
  { bg: "#5b21b6", fg: "#ede9fe" }, // violet
  { bg: "#155e75", fg: "#cffafe" }, // teal
  { bg: "#7c2d12", fg: "#ffedd5" }, // rust
  { bg: "#334155", fg: "#e2e8f0" }, // slate
] as const;

/** A small, stable string hash. Not cryptographic — it only has to be the same
 *  answer every time for the same code. */
function hash(value: string): number {
  let h = 0;
  for (let i = 0; i < value.length; i += 1) h = (h * 31 + value.charCodeAt(i)) | 0;
  return Math.abs(h);
}

export function paintFor(code: string): (typeof PAINT)[number] {
  return PAINT[hash(code) % PAINT.length];
}

/** Icons a sport's document may name, mapped to the glyph vocabulary. */
function glyphFor(icon: string | null | undefined): IconName | null {
  if (!icon) return null;
  const key = icon.replace(/^Icon/, "");
  const lower = key.charAt(0).toLowerCase() + key.slice(1);
  return lower in ICONS ? (lower as IconName) : null;
}

// Kept as a value so `in` above is a real check rather than a cast.
const ICONS: Record<string, true> = {
  layers: true,
  bracket: true,
  calendar: true,
  zap: true,
  swords: true,
  flag: true,
  route: true,
  target: true,
  circleDot: true,
  trophy: true,
  award: true,
  user: true,
  users: true,
  pin: true,
  hoof: true,
  tag: true,
  plus: true,
  list: true,
  sliders: true,
  search: true,
  waves: true,
  home: true,
};

export function SportGlyph({
  code,
  icon,
  size = 30,
}: {
  /** Undefined means "all sports" — a neutral mark rather than one sport's. */
  code: string | undefined;
  /** The icon its definition names, if the caller has it. */
  icon?: string | null;
  size?: number;
}) {
  const paint = code ? paintFor(code) : { bg: "var(--surface-3)", fg: "var(--ink-2)" };
  const name = glyphFor(icon);
  return (
    <span
      aria-hidden
      style={{
        width: size,
        height: size,
        flex: "none",
        display: "grid",
        placeItems: "center",
        borderRadius: Math.round(size / 3.6),
        background: paint.bg,
        color: paint.fg,
        // The initial is the fallback and it is a good one: two sports whose
        // icons are both "waves" are still told apart by their letter.
        fontSize: Math.round(size * 0.44),
        fontWeight: 600,
        letterSpacing: "-0.02em",
      }}
    >
      {name ? <Icon name={name} size={Math.round(size * 0.5)} /> : null}
      {!name && code ? code.charAt(0).toUpperCase() : null}
      {!name && !code ? <IconLayers size={Math.round(size * 0.5)} /> : null}
    </span>
  );
}
